/**
 * The pad's way into a menu or a dialogue (slice 6b). Keys reach a `Menu` or
 * a `Dialogue` directly (each listens while it is open); the pad's buttons
 * belong to the shell, which turns a press into a `NavCommand` (up, down,
 * confirm, close) and sends it here; the open menu or dialogue acts on it.
 * One channel per shell.
 *
 * Taps on a dialogue (slice 6b fix round): a pointer released within
 * `uiTiming.tapMaxMs` and less than `uiTiming.swipeMinPx` from where it went
 * down is a tap (`isTap`); a longer drag is a swipe and does nothing. The tap
 * that closes a window is consumed: `swallowNextClick` takes the click that
 * follows it (the browser's click comes after the pointer's release, when
 * the window under the finger is already gone), so nothing that comes back
 * under the finger (the prompt, a shop's row) acts on it.
 */
import { uiTiming } from './timing.ts';

/**
 * What a press asks of the open window. `left` and `right` (slice 8f) change the cursor row's value in a menu that
 * has adjustable rows (the packing panel's counts); every other window ignores them.
 */
export type NavCommand = 'up' | 'down' | 'confirm' | 'close' | 'left' | 'right';

export type MenuNav = {
  send(command: NavCommand): void;
  /** Listens for commands; returns the unsubscribe. */
  subscribe(listener: (command: NavCommand) => void): () => void;
};

export const createMenuNav = (): MenuNav => {
  const listeners = new Set<(command: NavCommand) => void>();
  return {
    send(command) {
      for (const l of [...listeners]) l(command);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

/** What a key does in an open menu or dialogue: arrows move, Enter, Space or E confirms, Escape or Backspace closes; null for any other key. */
export const navForKey = (key: string): NavCommand | null => {
  switch (key) {
    case 'ArrowUp':
      return 'up';
    case 'ArrowDown':
      return 'down';
    case 'Enter':
    case ' ':
    case 'e':
    case 'E':
      return 'confirm';
    case 'Escape':
    case 'Backspace':
      return 'close';
    default:
      return null;
  }
};

/** A key's step on an adjustable menu row (slice 8f): the left and right arrows; null for any other key. */
export const stepForKey = (key: string): 'left' | 'right' | null => (key === 'ArrowLeft' ? 'left' : key === 'ArrowRight' ? 'right' : null);

/** A menu row as its cursor sees it: a disabled row and a heading are passed over. */
export type CursorRow = { readonly key: string; readonly disabled?: boolean; readonly heading?: boolean };

/** Whether the cursor may rest on a row. */
export const selectable = (row: CursorRow | undefined): boolean => row !== undefined && row.disabled !== true && row.heading !== true;

/** The first row the cursor may rest on from `from`, going by `step` and wrapping round; `from` when there is none. */
export const firstEnabled = (rows: readonly CursorRow[], from: number, step: 1 | -1): number => {
  for (let k = 0; k < rows.length; k += 1) {
    const i = (((from + step * k) % rows.length) + rows.length) % rows.length;
    if (selectable(rows[i])) return i;
  }
  return from;
};

/** The row a menu's cursor starts on: `initial` when the cursor may rest there, else the first that it may. */
export const startRow = (rows: readonly CursorRow[], initial: string | undefined): string | null => {
  const asked = rows.findIndex((r) => r.key === initial && selectable(r));
  return rows[asked === -1 ? firstEnabled(rows, 0, 1) : asked]?.key ?? null;
};

/**
 * Where a menu's cursor is now (slice 8f): on its row, found by key, when the rows changed under it; when that row is
 * gone, the row the cursor may rest on at (or after) the place it was, and the cursor takes that row's key, so a row
 * that comes back later with the old key never pulls the cursor to it.
 */
export const cursorAt = (rows: readonly CursorRow[], key: string | null, lastIndex: number): { readonly index: number; readonly key: string | null } => {
  const found = rows.findIndex((r) => r.key === key);
  if (found !== -1) return { index: found, key };
  const index = firstEnabled(rows, Math.max(0, Math.min(lastIndex, rows.length - 1)), 1);
  return { index, key: rows[index]?.key ?? null };
};

/** Where and when a pointer went down or up (CSS pixels, the event's timestamp in ms). */
export type PointerMark = { readonly x: number; readonly y: number; readonly t: number };

/** A pointer's down and up as a tap over a menu or a dialogue: released soon enough and before it travelled a swipe's length. */
export const isTap = (down: PointerMark, up: PointerMark): boolean => Math.hypot(up.x - down.x, up.y - down.y) < uiTiming.swipeMinPx && up.t - down.t <= uiTiming.tapMaxMs;

/**
 * Takes the next click anywhere on the page (window, capture phase, before React sees it), for at most
 * `uiTiming.tapMaxMs`: the click a closing tap's release is followed by never reaches what is under the finger now.
 */
export const swallowNextClick = (): void => {
  let timer = 0;
  const off = (): void => {
    window.removeEventListener('click', swallow, true);
    window.clearTimeout(timer);
  };
  function swallow(e: MouseEvent): void {
    e.stopPropagation();
    e.preventDefault();
    off();
  }
  window.addEventListener('click', swallow, true);
  timer = window.setTimeout(off, uiTiming.tapMaxMs);
};
